import { hasAppAccess, type ProductFeatureMap } from '@tfa/shared'
import { useAuth } from './useAuth'
import { useCurator } from '../contexts/CuratorContext'

/**
 * Заперт ли клиент без подтверждённого тарифа.
 *
 * true → вместо приложения показываем блокирующий экран «заверши регистрацию»
 * (product=NONE: XL видит TFA-след, но доступа к курсу нет — запись
 * StudentCourse появляется только когда человек реально открыл курс).
 *
 * Куратор и имперсонация (куратор смотрит клиента / админ в режиме просмотра)
 * блокер НЕ видят: имперсонация — диагностический инструмент, прятать за стеной
 * ровно того клиента, чей доступ разбирают, бессмысленно, а кнопка «проверить»
 * дёргала бы реальный синк XL от имени админа.
 */
export function useAppAccessBlocked(): boolean {
  const { user, isCurator } = useAuth()
  const { viewingClientId, isAdminView } = useCurator()

  const u = user as Record<string, unknown> | undefined
  if (!u || isCurator || viewingClientId || isAdminView) return false
  // Защита от чужого payload (админ/куратор) в кэше ['user','me'].
  //
  // ОТСУТСТВИЕ type трактуем как клиента, а не как «чужой»: ответ
  // POST /auth/telegram (в отличие от /auth/me) поля не несёт, а useAuth кладёт
  // его прямо в кэш ['user','me'] со staleTime 5 минут. Со строгим сравнением
  // блокер молча не показывался всю первую сессию после входа через Telegram —
  // ровно то, как в приложение попадает живой клиент. Роли куратора/админа уже
  // отсечены флагами выше.
  if (u.type && u.type !== 'client') return false
  // До анкеты своя история — там показывается Onboarding.
  if (!u.onboardingCompleted) return false
  // Три тарифа: карта тарифа с сервера, дефолты — запасной путь.
  return !hasAppAccess(u.product as string | null, u.access as Partial<ProductFeatureMap> | null | undefined)
}

/**
 * Приостановлена ли программа клиента админом.
 *
 * true → вместо приложения показываем экран «доступ временно приостановлен».
 * Отличается от глобальной заморозки: ту клиент покупает сам на 7 дней и
 * приложением продолжает пользоваться; эту ставит админ, когда человек надолго
 * выбыл, и она закрывает приложение целиком до ручного снятия.
 *
 * Куратор и имперсонация экран НЕ видят — по той же причине, что и блокер выше:
 * прятать за стеной ровно того клиента, чью паузу разбирают, бессмысленно.
 */
export function useClientSuspended(): boolean {
  const { user, isCurator } = useAuth()
  const { viewingClientId, isAdminView } = useCurator()

  const u = user as Record<string, unknown> | undefined
  if (!u || isCurator || viewingClientId || isAdminView) return false
  if (u.type && u.type !== 'client') return false
  return !!u.suspendedAt
}
